import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApproveVatWriteOffStep,
  useRejectVatWriteOffStep,
  useReturnVatWriteOff,
  useSubmitVatWriteOff,
} from '../../lib/queries/vat-writeoff';
import { AppModal } from '../AppModal';
import { ApprovalTimeline, type ApprovalTimelineRow } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

// =============================================================================
// VatWriteOffApprovalPanel — the maker→reviewer(s)→approver chain for a deferred
// -VAT write-off request (docType 'VW', central approval engine, Design-Z).
//
// Mirrors ApBillApprovalPanel. The difference: B1 ships NO write-off detail-fetch
// hook (unlike useApBill), so this panel operates on a passed-in `request` — the
// open VatWriteOffRequest the worklist route (B5) resolved (findRequest returns
// the header + materialized `approvals` chain). The engine verbs (submit /
// approve-step / reject-step / return) drive the B1 hooks; final-step approval
// posts the write-off JV server-side. A 409 (state changed under us) is surfaced
// and the worklist cache invalidated so the caller can refresh.
// =============================================================================

// The open-request shape the route hands in — header + the materialized chain.
// Matches the API findRequest projection (apBillVoucherNo / vendorName joined on).
export interface VatWriteOffRequestView {
  id: string;
  voucherNo: string | null;
  apBillVoucherNo?: string | null;
  vendorName?: string | null;
  // Request lifecycle (DRAFT|POSTED|VOID) — distinct from the engine
  // approvalStatus (Design-Z). The B5 route gate reads it to pick the open request.
  status?: string | null;
  approvalStatus?: string | null;
  currentStep?: number | null;
  submittedBy?: string | null;
  submittedAt?: string | Date | null;
  approvedBy?: string | null;
  approvedAt?: string | Date | null;
  returnedReason?: string | null;
  approvals?: ApprovalTimelineRow[];
}

interface Props {
  companyId: string;
  request: VatWriteOffRequestView;
  onClose: () => void;
}

export function VatWriteOffApprovalPanel({ companyId, request, onClose }: Props) {
  const submitMut = useSubmitVatWriteOff(companyId);
  const approveMut = useApproveVatWriteOffStep(companyId);
  const rejectMut = useRejectVatWriteOffStep(companyId);
  const returnMut = useReturnVatWriteOff(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const voucherNo = request.voucherNo ?? '—';
  const subtitle = [request.apBillVoucherNo, request.vendorName].filter(Boolean).join(' · ');
  const approvalStatus = request.approvalStatus ?? null;
  const currentStep = request.currentStep ?? 0;
  const totalSteps = request.approvals?.length ?? 0;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(request.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: request.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: request.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: request.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['vat-suspense-worklist', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`สายอนุมัติการตัดจำหน่าย VAT — ${voucherNo}`}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="vat.writeoff.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="vat.writeoff.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && request.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="vw-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {request.returnedReason}
        </div>
      )}

      {/* awaiting-step hint */}
      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={request.approvals ?? []}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          request.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {request.submittedBy}
              {request.submittedAt ? ` · ${fmtDate(request.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          approvalStatus === 'APPROVED' && request.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {request.approvedBy}
              {request.approvedAt ? ` · ${fmtDate(request.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="vw-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการตัดจำหน่าย VAT"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
